/**
 * Renders a text primitive node.
 *
 * Resolves the variable binding (if any) and applies conditional styles.
 */

import { Box, Typography } from "@mui/material";
import React, { useMemo } from "react";
import { resolveBinding, resolveStyles } from "../layout-types";
import type {
  PrimitiveNode,
  InjectedData,
  LayoutDocument,
} from "../layout-types";
import { useLayoutRenderScale } from "../render-scale-context";
import {
  getTextGradientFillCss,
  hasTextGradientFill,
  isPrimitiveValue,
  normalizeListRowBindingPath,
} from "../../../../util/src/layout";
import { BindingExpression } from "../../../../util/src/blueprint/types";
import { resolveBindingExpression } from "../../../../util/src/blueprint";
import { formatBindingPathDisplayValue } from "../../../../util/src/position-rack";
import { getTextDecorationCssProps } from "../textDecorationCss.js";

interface LayoutTextProps {
  node: PrimitiveNode;
  data: InjectedData;
  document: LayoutDocument;
  bindingPatch?: BindingExpression;
}

const toCssLength = (value: unknown): string | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return `${Math.max(0, value)}px`;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    return value.trim();
  }
  return undefined;
};

const getResolvedBorderSx = (
  resolved: Record<string, unknown>,
): Record<string, unknown> => ({
  border: resolved.border as string | undefined,
  borderTop: resolved.borderTop as string | undefined,
  borderRight: resolved.borderRight as string | undefined,
  borderBottom: resolved.borderBottom as string | undefined,
  borderLeft: resolved.borderLeft as string | undefined,
  borderRadius: toCssLength(resolved.borderRadius),
  borderTopLeftRadius: toCssLength(resolved.borderTopLeftRadius),
  borderTopRightRadius: toCssLength(resolved.borderTopRightRadius),
  borderBottomRightRadius: toCssLength(resolved.borderBottomRightRadius),
  borderBottomLeftRadius: toCssLength(resolved.borderBottomLeftRadius),
});

const getTextJustifyContent = (
  rawAlign: unknown,
): "flex-start" | "center" | "flex-end" => {
  const align = String(rawAlign ?? "left")
    .trim()
    .toLowerCase();
  if (align === "center") return "center";
  if (align === "right") return "flex-end";
  return "flex-start";
};

const LayoutText: React.FC<LayoutTextProps> = ({
  node,
  data,
  bindingPatch,
}) => {
  const resolved = resolveStyles(node.style, data);
  const renderScale = useLayoutRenderScale();

  const text = node.text ?? "";
  const valueKind = useMemo(
    () => node.valueKind ?? "primitive",
    [node.valueKind],
  );
  const binding = node.dataBinding
    ? resolveBinding(
        {
          path: normalizeListRowBindingPath(node.dataBinding.path),
        },
        data,
      )
    : undefined;

  const resolveBindingPatch = useMemo(() => {
    if (!bindingPatch) return undefined;
    return resolveBindingExpression(bindingPatch, data);
  }, [bindingPatch, data]);

  const isHidden = resolved.visibility === false;
  if (isHidden) return null;

  const isFitContentWidth = node.textWidthMode === "fitContent";
  // Gradient on text → clip to glyphs; solid color would cover the fill
  const gradientFill = hasTextGradientFill(resolved.backgroundImage)
    ? getTextGradientFillCss(String(resolved.backgroundImage))
    : undefined;

  const displayText =
    valueKind === "binding"
      ? bindingPatch
        ? isPrimitiveValue(resolveBindingPatch)
          ? formatBindingPathDisplayValue(
              node.dataBinding?.path,
              resolveBindingPatch,
            )
          : ""
        : isPrimitiveValue(binding)
          ? formatBindingPathDisplayValue(node.dataBinding?.path, binding)
          : ""
      : text;

  return (
    <Box
      sx={{
        width: isFitContentWidth ? "max-content" : "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // Keep overflow visible so drop-shadow / glyph edges are not clipped
        overflow: gradientFill ? "visible" : "hidden",
      }}
    >
      <Typography
        sx={{
          fontSize:
            ((resolved.fontSize as number | undefined) ?? 16) * renderScale,
          fontFamily: resolved.fontFamily as string | undefined,
          fontWeight: resolved.fontWeight as string | undefined,
          color: gradientFill
            ? undefined
            : (resolved.color as string | undefined),
          backgroundColor: resolved.backgroundColor as string | undefined,
          opacity: resolved.opacity as number | undefined,
          ...getResolvedBorderSx(resolved),
          width: isFitContentWidth ? "max-content" : "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          boxSizing: "border-box",
          justifyContent: getTextJustifyContent(resolved.textAlign),
          textAlign:
            (resolved.textAlign as "left" | "center" | "right" | undefined) ??
            "left",
          textTransform: resolved.textTransform as
            | "none"
            | "capitalize"
            | "uppercase"
            | "lowercase"
            | undefined,
          whiteSpace: resolved.whiteSpace as string | undefined,
          ...getTextDecorationCssProps(resolved, renderScale),
          lineHeight: 1.2,
        }}
      >
        {/* Gradient on a text-sized span so the fill maps to glyph height, not the node box */}
        {gradientFill ? (
          <Box
            component="span"
            sx={{
              display: "inline-block",
              // line-height 1 maps the gradient to the em-box (letter tops stay white)
              lineHeight: 1,
              ...gradientFill,
            }}
          >
            {displayText}
          </Box>
        ) : (
          displayText
        )}
      </Typography>
    </Box>
  );
};

export default LayoutText;
